import { Injectable } from "@angular/core";
import { Platform } from 'ionic-angular';
import 'rxjs/add/operator/toPromise';
import { AngularFireAuth } from 'angularfire2/auth';
import firebase from 'firebase';
import { GooglePlus } from '@ionic-native/google-plus';
import { UserModel, ProfileModel, LocationDetails } from '../../models/profile.model';
import { HttpHeaders, HttpClient } from "@angular/common/http";
import { environment } from '../../environment/environment';

@Injectable()
export class FirebaseAuthService {

  userModel: UserModel;
  backendURL = environment.backendURL;

  constructor(
    public afAuth: AngularFireAuth,
    public googlePlus: GooglePlus,
    public platform: Platform,
    private http: HttpClient
  ) { }

  doLogin(value) {
    return new Promise<any>((resolve, reject) => {
      firebase.auth().signInWithEmailAndPassword(value.email, value.password)
        .then(res => {

          if (!res.user.emailVerified) {
            this.afAuth.auth.signOut();

            return reject({
              errorType: "notVerified",
              data: res
            });
          }

          resolve(res);
        }, err => reject(err))
    })
  }

  doLogout() {
    return new Promise<void>((resolve, reject) => {
      if (firebase.auth().currentUser) {
        this.afAuth.auth.signOut();
        resolve();
      }
      else {
        reject();
      }
    });
  }

  getCurrentUser(): Promise<UserModel> {
    var userModel = new UserModel;
    return new Promise<UserModel>((resolve, reject) => {
      firebase.auth().onAuthStateChanged(function (user) {

        if (user) {
          if (!user.emailVerified) return reject('No user logged in');

          if (!user.photoURL) {
            userModel.id = user.uid;
            userModel.image = 'http://dsi-vd.github.io/patternlab-vd/images/fpo_avatar.png';
            userModel.name = user.displayName;
            userModel.provider = user.providerData[0].providerId;
            userModel.email = user.email;
            return resolve(userModel);
          }
          else {
            userModel.id = user.uid;
            userModel.image = user.photoURL;
            userModel.name = user.displayName;
            userModel.provider = user.providerData[0].providerId;
            userModel.email = user.email;
            return resolve(userModel);
          }

        } else {
          reject('No user logged in');
        }
      })
    });
  }

  getTribalCreditBalance() {

    return new Promise<number>((resolve, reject) => {
      firebase.auth().onAuthStateChanged(function (user) {

        if (user) {
          if (!user.emailVerified) return reject('No user logged in');

          firebase.database().ref('/users').child(user.uid).child('user').child('tribalCreditBalance').once('value', snapshot => {
            if (snapshot && snapshot.val()) {
              return resolve(snapshot.val());
            }
          })
        } else {
          reject('No user logged in');
        }
      })
    });
  }

  getCurrentUserId(): Promise<string> {

    return new Promise<string>((resolve, reject) => {
      firebase.auth().onAuthStateChanged(function (user) {

        if (user) {
          // Allow unverified users to proceed for public profile and basic access
          return resolve(user.uid);
        } else {
          // Resolve with null when auth not ready yet
          return resolve(null);
        }
      });
    });
  }

  updateFormValues(value: any): any {
    return new Promise<void>((resolve, reject) => {
      var user = firebase.auth().currentUser;
      user.updateProfile({
        displayName: value.name,
        photoURL: user.photoURL
      }).then(res => {
        resolve()
      }, err => reject())
    })
  }

  checkForExistingAccount(uid): Promise<boolean> {
    return new Promise((resolve, reject) => {
      firebase.database().ref('/users/' + uid).once('value', snapshot => {
        if (snapshot.val()) {
          if (snapshot.val().user) {
            return resolve(true);
          }
        }
        return resolve(false);
      });
    });
  }

  async createNewUser(user, locationDetails?: LocationDetails): Promise<Object> {
    const profile = new ProfileModel();
    profile.mapUserToProfile(user, locationDetails);

    // Ensure locationDetails is stored the same way legacy code expects
    if (locationDetails) {
      profile.user.locationDetails = locationDetails;
    }

    // Direct write to Realtime Database – no Cloud Function required
    return new Promise((resolve, reject) => {
      const userRef = firebase.database().ref('/users/' + user.uid);
      userRef.set(profile)
        .then(() => resolve({ result: 'success' }))
        .catch(error => reject(error));
    });
  }

  sendEmailVerificationMail(user) {
    if (user && user.sendEmailVerification) {
      user.sendEmailVerification().then(() => {
        // Verification email sent
      }).catch(error => {
        // Handle error if needed
      });
    }
  }

  updateExistingUser(user): Promise<boolean> {
    var date = new Date();
    var ref = firebase.database().ref('/users/' + user.uid).child('user');

    ref.child('loginHistory').push().set(date.toString());

    return new Promise((resolve, reject) => {
      ref.update({
        image: user.photoURL,
        lastLogin: date.toString(),
        updatedOn: date.toString()
      }).then(function () {
        resolve(user);
      });
    });
  }

  doRegister(user, locationDetails: LocationDetails, value: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      firebase.auth().createUserWithEmailAndPassword(value.email, value.password)
        .then(resp => {
          resp.user.updateProfile({
            displayName: user.name,
            photoURL: ""
          });

          this.checkForExistingAccount(resp.user.uid).then(response => {
            if (response) {
              this.updateExistingUser(resp).then(response => {
                this.sendEmailVerificationMail(resp.user);
                return resolve(response);
              });
            } else {
              user.uid = resp.user.uid;
              this.createNewUser(user, locationDetails).then(response => {
                this.sendEmailVerificationMail(resp.user);
                return resolve(response);
              });
            }
          });
          this.sendEmailVerificationMail(resp.user);
          return resolve(resp);
        }, err => {
          return reject(err)
        });
    })
  }

  doPasswordReset(email) {

    return new Promise<void>((resolve, reject) => {
      firebase.auth().sendPasswordResetEmail(email).then(() => {
        resolve();
      }).catch(function (error) {
        reject(error);
      });
    });
  }

  verifyCaptcha(response: string): Promise<any> {

    try {
      // Add defensive check for response
      if (!response) {
        return Promise.reject(new Error('No captcha response provided'));
      }

      // Use the deployed Firebase Function URL
      const functionUrl = 'https://us-central1-tribalchatapp-5aec7.cloudfunctions.net/verifyCaptcha';

      let data = {
        response: response
      };

      var headers = new HttpHeaders();
      headers.append('Content-Type', 'application/json');

      return new Promise((resolve, reject) => {

        this.http.post(functionUrl, data, { headers })
          .subscribe((res: any) => {
            console.log('Firebase Function captcha response:', res);

            if (res && res.result == 'success') {
              return resolve(res);
            } else if (res && res.result == 'failed') {
              console.error('Captcha failed with reason:', res.reason);
              return reject(new Error(`Captcha verification failed: ${res.reason}`));
            }
            return reject(new Error('Captcha verification failed'));
          }, error => {
            console.error('Captcha verification error:', error);
            reject(error);
          });
      });
    }
    catch (error) {
      return Promise.reject(error.message);
    }
  }

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }
}


// WEBPACK FOOTER //
// ./src/pages/firebase-integration/firebase-auth.service.ts